import { useId, type ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { HFCard } from '../../atoms/HFCard';
import { HFSkeleton } from '../../atoms/HFSkeleton';
import { composeSx } from '../../utils/composeSx';

export type HFKpiTileProps = {
  label: string;
  value?: ReactNode;
  unit?: ReactNode;
  loading?: boolean;
  /** Short hint shown under a dash. Never renders 0 in place of a failed value. */
  error?: string;
  sx?: SxProps<Theme>;
};

const valueSx = {
  m: 0,
  fontSize: 28,
  fontWeight: 800,
  lineHeight: 1.1,
  fontVariantNumeric: 'tabular-nums',
} as const;

export function HFKpiTile({ label, value, unit, loading = false, error, sx }: HFKpiTileProps) {
  const hintId = useId();
  const showError = !loading && error != null && error !== '';

  return (
    <HFCard
      density="compact"
      aria-busy={loading || undefined}
      aria-describedby={showError ? hintId : undefined}
      sx={composeSx({ height: 'auto' }, sx)}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          component="div"
          sx={{ m: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
        >
          {label}
        </Typography>
        {loading ? (
          <HFSkeleton variant="text" width={64} sx={{ fontSize: 28 }} />
        ) : showError ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="div" sx={{ ...valueSx, color: 'text.secondary' }}>
              —
            </Typography>
            <Typography id={hintId} variant="caption" sx={{ m: 0, color: 'error.main', display: 'block' }}>
              {error}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography component="div" sx={{ ...valueSx, color: 'text.primary' }}>
              {value}
            </Typography>
            {unit != null && unit !== '' ? (
              <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {unit}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
    </HFCard>
  );
}
